/**
 * Applies the tournament's look (palette, accent and lettering, picked on the calibration screen) to the
 * page. Every screen calls useLook(state.config.look) once.
 */

import { useEffect } from "react";
import type { Accent, Lettering, Look, Palette } from "../../../engine/types";

export const PALETTES: { id: Palette; label: string }[] = [
  { id: "white", label: "White" },
  { id: "cream", label: "Cream" },
  { id: "yellow", label: "Yellow ground" },
  { id: "cards-on-dark", label: "Cards on dark" },
];

export const ACCENTS: { id: Accent; label: string }[] = [
  { id: "pink", label: "Tangerine" },
  { id: "cyan", label: "Cyan" },
  { id: "ink", label: "Black" },
];

export const LETTERINGS: { id: Lettering; label: string }[] = [
  { id: "grotesque", label: "Grotesque" },
  { id: "round", label: "Round" },
  { id: "condensed", label: "Condensed" },
  { id: "caps", label: "Capitals" },
];

export const DEFAULT_LOOK: Look = { palette: "white", accent: "pink" };
export const DEFAULT_LETTERING: Lettering = "grotesque";

/** A lettering to try on this screen only, from the address: ?type=round. It wins over the saved one. */
const TRY = new URLSearchParams(location.search).get("type");
const tryLettering = LETTERINGS.find((l) => l.id === TRY)?.id ?? null;

export function applyLook(look: Look): void {
  const root = document.documentElement;
  if (look.palette === "white") root.removeAttribute("data-palette");
  else root.setAttribute("data-palette", look.palette);
  if (look.accent === "pink") root.removeAttribute("data-accent");
  else root.setAttribute("data-accent", look.accent);
  const lettering = tryLettering ?? look.lettering ?? DEFAULT_LETTERING;
  if (lettering === DEFAULT_LETTERING) root.removeAttribute("data-type");
  else root.setAttribute("data-type", lettering);
}

export function useLook(look: Look | undefined): void {
  const palette = look?.palette ?? DEFAULT_LOOK.palette;
  const accent = look?.accent ?? DEFAULT_LOOK.accent;
  const lettering = look?.lettering ?? DEFAULT_LETTERING;
  useEffect(() => {
    applyLook({ palette, accent, lettering });
  }, [palette, accent, lettering]);
}
